/**
 * ProjectBadge - Dropdown combobox for selecting/creating projects
 *
 * Used in Chat page and CompactContextBar to select active project.
 * Shows "Project +" when no project selected, project name when selected.
 */

import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { FolderPlus, Folder, X, ChevronDown, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getProjects } from '@/api/projects';
import type { Project } from '@/types/context';
import { CreateProjectDialog } from './CreateProjectDialog';

interface ProjectBadgeProps {
  /** Currently selected project ID */
  selectedProjectId?: string | null;
  /** Called when project selection changes */
  onProjectChange: (projectId: string | null) => void;
  /** Require auth before allowing changes */
  requireAuth?: boolean;
  /** Callback when auth is required */
  onRequireAuth?: () => void;
  /** Disable interactions */
  disabled?: boolean;
  /** Additional class names */
  className?: string;
}

export function ProjectBadge({
  selectedProjectId,
  onProjectChange,
  requireAuth = false,
  onRequireAuth,
  disabled,
  className,
}: ProjectBadgeProps) {
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // Load projects on mount
  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    setIsLoading(true);
    try {
      const data = await getProjects();
      setProjects(data);
    } catch (error) {
      console.error('Failed to load projects:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const requireAuthIfNeeded = () => {
    if (requireAuth) {
      onRequireAuth?.();
      return true;
    }
    return false;
  };

  const selectedProject = projects.find(p => p.id === selectedProjectId);

  const handleProjectSelect = (projectId: string | null) => {
    if (requireAuthIfNeeded()) return;
    onProjectChange(projectId);
    setDropdownOpen(false);
  };

  const handleProjectCreated = (project: Project) => {
    if (requireAuthIfNeeded()) return;
    setProjects(prev => [project, ...prev]);
    onProjectChange(project.id);
    setShowCreateDialog(false);
  };

  const handleCreateClick = () => {
    if (requireAuthIfNeeded()) return;
    setDropdownOpen(false);
    setShowCreateDialog(true);
  };

  const handleDropdownOpenChange = (open: boolean) => {
    if (open && requireAuthIfNeeded()) return;
    setDropdownOpen(open);
  };

  return (
    <>
      <DropdownMenu open={dropdownOpen} onOpenChange={handleDropdownOpenChange}>
        <DropdownMenuTrigger asChild disabled={disabled}>
          {selectedProject ? (
            <Badge
              variant="outline"
              className={cn(
                "text-xs cursor-pointer gap-1 pr-1 flex-shrink-0",
                "border-muted-foreground/20 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors",
                className
              )}
            >
              <Folder className="h-3 w-3" />
              {selectedProject.name}
              <button
                className="ml-0.5 hover:text-foreground"
                onClick={(e) => {
                  e.stopPropagation();
                  handleProjectSelect(null);
                }}
                disabled={disabled}
              >
                ×
              </button>
            </Badge>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              disabled={disabled}
              className={cn(
                "h-6 px-2 text-xs text-muted-foreground hover:text-foreground",
                "border border-dashed border-muted-foreground/30 hover:border-muted-foreground/50",
                className
              )}
            >
              <Folder className="h-3 w-3 mr-1" />
              Project +
            </Button>
          )}
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end" className="w-56">
          {/* Create new project option */}
          <DropdownMenuItem
            onClick={handleCreateClick}
            className="gap-2 text-primary"
          >
            <FolderPlus className="h-4 w-4" />
            New project
          </DropdownMenuItem>

          {projects.length > 0 && <DropdownMenuSeparator />}

          {/* Clear selection option (only if project is selected) */}
          {selectedProjectId && (
            <>
              <DropdownMenuItem
                onClick={() => handleProjectSelect(null)}
                className="gap-2 text-muted-foreground"
              >
                <X className="h-4 w-4" />
                No project
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          )}

          {/* Project list */}
          {isLoading ? (
            <DropdownMenuItem disabled className="text-muted-foreground">
              Loading projects...
            </DropdownMenuItem>
          ) : projects.length === 0 ? (
            <DropdownMenuItem disabled className="text-muted-foreground">
              No projects yet
            </DropdownMenuItem>
          ) : (
            projects.map((project) => (
              <DropdownMenuItem
                key={project.id}
                onClick={() => handleProjectSelect(project.id)}
                className="gap-2"
              >
                <Folder className="h-4 w-4" />
                <span className="flex-1 truncate">{project.name}</span>
                {project.id === selectedProjectId && (
                  <Check className="h-4 w-4 text-primary" />
                )}
              </DropdownMenuItem>
            ))
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Create Project Dialog */}
      <CreateProjectDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        onProjectCreated={handleProjectCreated}
      />
    </>
  );
}
